import React, { useState, useEffect } from 'react';
import { api } from '../api';
import FormSettingsPanel from './FormSettingsPanel';
import PublicHolidaysSettings from './PublicHolidaysSettings';
import ContractorManagement from './ContractorManagement';
import AgreementsPanel from './AgreementsPanel';
import CustomersTab from './CustomersTab';
import EmailServerSettings from './EmailServerSettings';
import { BUFFING_TIERS } from './BuffingFormV2';

export default function AdminSettingsPage() {
  const [subTab, setSubTab] = useState('Contractors');
  const subTabs = ['Contractors', 'Customers', 'Agreements', 'Insurance', 'Buffing %', 'Form Settings', 'Public Holidays', 'Email'];

  return (
    <div>
      {/* Sub-tab nav */}
      <div style={{ borderBottom: '2px solid #e5e7eb', background: '#f9fafb', padding: '0 24px' }}>
        <div style={{ display: 'flex', gap: 0 }}>
          {subTabs.map(t => (
            <button key={t} onClick={() => setSubTab(t)} style={{
              padding: '10px 20px', border: 'none', background: 'transparent',
              fontSize: 13, fontWeight: 600,
              color: subTab === t ? '#4A7A91' : '#9ca3af',
              borderBottom: subTab === t ? '2px solid #4A7A91' : '2px solid transparent',
              cursor: 'pointer', marginBottom: -2,
            }}>{t}</button>
          ))}
        </div>
      </div>

      <div>
        {subTab === 'Contractors'     && <ContractorManagement />}
        {subTab === 'Customers'       && <CustomersTab />}
        {subTab === 'Agreements'      && <AgreementsPanel />}
        {subTab === 'Insurance'       && <InsuranceSettings />}
        {subTab === 'Buffing %'       && <BuffingWeightsSettings />}
        {subTab === 'Form Settings'   && <FormSettingsPanel />}
        {subTab === 'Public Holidays' && <PublicHolidaysSettings />}
        {subTab === 'Email'           && <EmailServerSettings />}
      </div>
    </div>
  );
}

// ── Buffing percentages ────────────────────────────────────────────────────
// The Buffing form offers these as buttons; whichever is picked is what gets
// charged. They are not derived from the parts — the same two panels are a
// different job on a big car than on a small one, so the person doing the
// work chooses, and the parts they tick are the record of what was done.
function BuffingWeightsSettings() {
  const [tiers, setTiers] = useState(BUFFING_TIERS);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    api.getBuffingTiers()
      .then(r => Array.isArray(r.tiers) && r.tiers.length && setTiers(r.tiers))
      .catch(() => {});
  }, []);

  const setAt = (i, v) => setTiers(t => t.map((x, j) => (j === i ? v : x)));

  const clean = tiers.map(t => Number(t) || 0).filter(t => t > 0 && t <= 100);
  const sorted = [...clean].sort((a, b) => a - b);
  const duplicated = new Set(clean).size !== clean.length;
  const valid = clean.length === tiers.length && !duplicated;

  async function save() {
    setSaving(true); setMsg('');
    try {
      await api.saveBuffingTiers(sorted);
      setTiers(sorted);
      setMsg('Saved.');
    } catch (err) {
      setMsg(err.message || 'Save failed.');
    } finally { setSaving(false); }
  }

  return (
    <div style={{ padding: 24, maxWidth: 560 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 800, color: '#1e3a4a' }}>Buffing percentages</h3>
      <p style={{ margin: '0 0 18px', fontSize: 13, color: '#6b7280' }}>
        The options offered on the Buffing form. The one picked is what gets charged and paid —
        the parts ticked alongside it are the record of what was buffed, not a calculation.
      </p>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {tiers.map((t, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="number" min="1" max="100" value={t}
              onChange={e => setAt(i, e.target.value)}
              style={{ width: 84, padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 16, fontWeight: 700, textAlign: 'right' }} />
            <span style={{ fontSize: 14, color: '#9ca3af' }}>%</span>
            {tiers.length > 2 && (
              <button onClick={() => setTiers(t2 => t2.filter((_, j) => j !== i))}
                style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: '0 2px' }}
                aria-label="Remove">×</button>
            )}
          </div>
        ))}
        {tiers.length < 6 && (
          <button onClick={() => setTiers(t => [...t, ''])}
            style={{ padding: '10px 16px', border: '1.5px dashed #cbd5e1', borderRadius: 8, background: '#fff', color: '#4A7A91', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            + Add
          </button>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 20 }}>
        {!valid && (
          <span style={{ fontSize: 13, fontWeight: 700, color: '#b45309' }}>
            {duplicated ? 'Two options are the same.' : 'Each option must be between 1 and 100.'}
          </span>
        )}
        <button onClick={save} disabled={saving || !valid} style={{
          marginLeft: 'auto', padding: '10px 22px', border: 'none', borderRadius: 10,
          background: (saving || !valid) ? '#d1d5db' : '#4A7A91', color: '#fff', fontWeight: 800, fontSize: 14,
          cursor: (saving || !valid) ? 'not-allowed' : 'pointer',
        }}>{saving ? 'Saving…' : 'Save'}</button>
        {msg && <span style={{ fontSize: 13, color: '#6b7280' }}>{msg}</span>}
      </div>
    </div>
  );
}

// ── Insurance Settings ─────────────────────────────────────────────────────
// Two halves: broker / help config (left), team overview (right). Pulling
// the overview onto this page gives the boss one screen to scan who needs
// follow-up without opening every contractor edit modal.
const INS_STATUS_META = {
  pending:  { label: 'Pending',  bg: '#eef2f6', fg: '#5b6b7b' },
  valid:    { label: 'Valid',    bg: '#dcfce7', fg: '#15803d' },
  expiring: { label: 'Expiring', bg: '#fef3c7', fg: '#92400e' },
  expired:  { label: 'Expired',  bg: '#fee2e2', fg: '#991b1b' },
};

function InsuranceSettings() {
  const [cfg, setCfg]             = useState({ broker_pl_url: '', broker_ip_url: '', help_whatsapp: '' });
  const [overview, setOverview]   = useState([]);
  const [saving, setSaving]       = useState(false);
  const [savedAt, setSavedAt]     = useState(null);
  const [busyDigest, setBusyDigest] = useState(false);

  async function load() {
    try {
      const [c, ov] = await Promise.all([
        api.insuranceConfig(),
        api.insuranceAdminOverview(),
      ]);
      setCfg({
        broker_pl_url: c.broker_pl_url || '',
        broker_ip_url: c.broker_ip_url || '',
        help_whatsapp: c.help_whatsapp || '',
      });
      setOverview(ov);
    } catch (err) {
      console.error('Insurance settings load failed:', err);
    }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    try {
      await api.saveInsuranceConfig(cfg);
      setSavedAt(new Date());
    } catch (err) {
      alert(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function fireDigest() {
    if (!window.confirm('Send the expiry-reminder email digest to Michel now?')) return;
    setBusyDigest(true);
    try {
      const r = await api.triggerInsuranceDigest();
      if (r?.skipped) alert('Nothing to report — no expired/expiring/missing certificates.');
      else            alert(`Digest sent (${r?.totalIssues} items).`);
    } catch (err) {
      alert(err.message || 'Failed');
    } finally {
      setBusyDigest(false);
    }
  }

  // Quick counts for the header strip.
  const counts = { valid: 0, expiring: 0, expired: 0, pending: 0 };
  overview.forEach(row => {
    counts[row.pl?.status || 'pending']++;
    counts[row.ip?.status || 'pending']++;
  });

  return (
    <div style={{ padding: 20, maxWidth: 1080, margin: '0 auto' }}>

      {/* Counts strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
        {(['valid', 'expiring', 'expired', 'pending']).map(k => (
          <div key={k} style={{
            background: INS_STATUS_META[k].bg, color: INS_STATUS_META[k].fg,
            padding: '12px 14px', borderRadius: 10, fontWeight: 700,
          }}>
            <div style={{ fontSize: 10.5, letterSpacing: 0.8, textTransform: 'uppercase', opacity: 0.85 }}>
              {INS_STATUS_META[k].label}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{counts[k]}</div>
          </div>
        ))}
      </div>

      {/* Broker URLs + Help */}
      <div className="card" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 18, marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 14, color: '#1e3a4a' }}>Broker & support links</h3>
        <p style={{ margin: '0 0 14px', fontSize: 12, color: '#6b7280' }}>
          These show up in the contractor's Insurance page. Update without a redeploy.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
          <Field label="Public Liability — broker URL"
                 value={cfg.broker_pl_url}
                 onChange={v => setCfg({ ...cfg, broker_pl_url: v })}
                 placeholder="https://bizcover.com.au/..." />
          <Field label="Personal Accident / IP — broker URL"
                 value={cfg.broker_ip_url}
                 onChange={v => setCfg({ ...cfg, broker_ip_url: v })}
                 placeholder="https://bizcover.com.au/..." />
          <Field label="Support WhatsApp (E.164 format, e.g. +61494304689)"
                 value={cfg.help_whatsapp}
                 onChange={v => setCfg({ ...cfg, help_whatsapp: v })}
                 placeholder="+61494304689" />
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 14, alignItems: 'center' }}>
          <button onClick={save} disabled={saving}
                  style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#1e3a4a', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={fireDigest} disabled={busyDigest}
                  style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #CBD5E1', background: '#fff', color: '#475569', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
            {busyDigest ? 'Sending…' : '📧 Send digest now'}
          </button>
          {savedAt && (
            <span style={{ fontSize: 12, color: '#15803d' }}>✓ saved</span>
          )}
        </div>
      </div>

      {/* Team overview */}
      <div className="card" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 18 }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 14, color: '#1e3a4a' }}>Team overview</h3>
        {overview.length === 0 ? (
          <div style={{ color: '#9ca3af', fontSize: 13 }}>No active contractors.</div>
        ) : (
          <table style={{ width: '100%', fontSize: 12.5, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F8FAFC', color: '#475569', fontSize: 10.5, letterSpacing: 0.6, textTransform: 'uppercase', fontWeight: 700 }}>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Contractor</th>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Public Liability</th>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Personal Accident / IP</th>
              </tr>
            </thead>
            <tbody>
              {overview.map(row => (
                <tr key={row.sub.id} style={{ borderTop: '1px solid #F1F5F9' }}>
                  <td style={{ padding: '6px 8px', color: '#0F172A', fontWeight: 600 }}>{row.sub.name}</td>
                  <OverviewCell cert={row.pl} />
                  <OverviewCell cert={row.ip} />
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>
        {label}
      </label>
      <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
             style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
    </div>
  );
}

function OverviewCell({ cert }) {
  const status = cert?.status || 'pending';
  const sMeta = INS_STATUS_META[status];
  const validade = cert?.validade ? String(cert.validade).slice(0, 10).split('-').reverse().join('/') : null;
  return (
    <td style={{ padding: '6px 8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{
          fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 999,
          background: sMeta.bg, color: sMeta.fg, whiteSpace: 'nowrap',
        }}>{sMeta.label}</span>
        {validade && <span style={{ color: '#64748B', fontSize: 11 }}>· exp {validade}</span>}
        {cert?.id && (
          <a href={api.insuranceFileUrl(cert.id)} target="_blank" rel="noreferrer"
             style={{ color: '#4A7A91', fontWeight: 600, fontSize: 11, marginLeft: 'auto' }}>Open</a>
        )}
      </div>
    </td>
  );
}
